import { useApiServices } from '../providers/useApiServices';
import type { Fetcher } from '../rest-api-theboatdb/generated/theboatdb-typed-openapi';
import {
  THEBOATDB_ERROR_CODES,
  TheBoatDbApiError,
  toTheBoatDbApiError,
} from '../rest-api-theboatdb/theboatdb-api-error';
import { applyDeclaredHeaders, buildRequestBody, type RestFetcherInput } from './rest-request';

/**
 * The fetcher for TheBoatDB API — a different service from the Laravel backend,
 * with a different credential and a different error contract, which is why it is
 * a fetcher of its own rather than an option on `rest-api-fetcher.ts`.
 *
 * Two differences do the work:
 *
 * 1. **The token is a plain Clerk session token, with no JWT template.** The
 *    Laravel API takes a `one_hour_token`; this service verifies the session
 *    token itself against Clerk's JWKS (`require-clerk-auth.middleware.ts`), and
 *    a template token is a different audience that fails verification. Passing
 *    the wrong one produces a 401 that looks exactly like an expired session, so
 *    it is worth being explicit about.
 *
 * 2. **Failures are mapped to `TheBoatDbApiError` here, once.** The service
 *    answers every error with a machine-readable `code` from a closed set, and
 *    every screen branches on that code. Throwing from the fetcher rather than
 *    letting the client raise a generic `TypedStatusError` means a hook never
 *    has to unwrap a response to find out what went wrong.
 */
export const useTheBoatDbApiFetcher = (): Fetcher['fetch'] => {
  const { getToken } = useApiServices();

  return async (input: RestFetcherInput): Promise<Response> => {
    // No `template`: see (1) above.
    const token = await getToken();

    // A signed-out caller is refused here rather than sent as `Bearer null`,
    // which would come back as a 401 the screen would report as "the service
    // rejected your session" — true of the wire, misleading about the cause.
    if (!token) {
      throw new TheBoatDbApiError({
        code: THEBOATDB_ERROR_CODES.UNAUTHORIZED,
        message: 'Your session has expired. Sign in again to continue.',
        status: 401,
      });
    }

    const headers = new Headers();

    if (input.urlSearchParams) {
      input.url.search = input.urlSearchParams.toString();
    }

    const body = buildRequestBody(input, headers);

    headers.set('Authorization', `Bearer ${token}`);
    applyDeclaredHeaders(input, headers);

    const response = await fetch(input.url, {
      method: input.method.toUpperCase(),
      ...(body === undefined ? {} : { body }),
      headers,
      ...input.overrides,
    });

    if (!response.ok) {
      throw await toTheBoatDbApiError(response);
    }

    return response;
  };
};
